import { useMutation, useQuery } from "@tanstack/react-query";
import { Radio, Text } from "@ui-kitten/components";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import Button from "components/Button";
import Container from "components/Container";
import Input from "components/Input";
import Spacer from "components/Spacer";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
  TokenReceivedOutcome,
} from "components/StripePaymentForm/StripePaymentForm";
import { formattedDate } from "components/TextInput";
import { loginSucceed } from "features/slices/authSlice";
import {
  AccountDetails,
  BillingInterval,
  JwtLoginSuccessResponse,
  PlanPackageDetails,
  PlanPackageUserFieldDetails,
  PrimaryUser,
  TenantUserFieldDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import useAuth from "hooks/useAuth";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSignupCharge from "hooks/useSignupCharge";
import useTenantUserFields from "hooks/useTenantUserFields";
import { ShopProductV3 } from "lib/sanity.types";
import i18n from "plugins/localization";
import {
  AddAccountMemberInput,
  addMembersFetcher,
  addSubscriptionFetcher,
  fetchSignupChargeRequest,
  updateBillingFetcher,
} from "queries/account.queries";
import { useFetchAgreementsQuery } from "queries/agreement.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, View } from "react-native";
import { setUserToken } from "screens/Register/registerUtils";
import accountServices from "services/account.services";
import userServices from "services/user.services";
import { formatCurrency } from "utils/appUtils";
import { getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import CardOnFileV3 from "./components/CardOnFileV3";
import PaymentBreakdownV3 from "./components/PaymentBreakdownV3";
import UpgradeTileV3 from "./components/UpgradeTileV3";
import {
  CheckoutUpgrade,
  fullPriceOf,
  isValidEmail,
  LINE,
  LiveVariant,
  monthlyPriceOf,
  PAPER,
} from "./helpers";

type AnyUserFieldDetails = TenantUserFieldDetails | PlanPackageUserFieldDetails;

// Dates are typed with the MM/DD/YYYY auto-mask (formattedDate).
const isValidMaskedDate = (value: string) =>
  /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim());
// Backend user fields expect DATE values as yyyy-MM-dd.
const toIsoDate = (maskedDate: string) => {
  const [month, day, year] = maskedDate.trim().split("/");
  return `${year}-${month}-${day}`;
};

export interface PurchaseSummary {
  productName: string;
  variantLabel: string;
  kidsClub: boolean;
  memberNames: string[];
  // Confirmation heading for memberNames (e.g. "Your seats" for season
  // tickets); defaults to "Members on this registration".
  memberNamesTitle?: string;
  amountLabel: string;
}

interface ChildForm {
  firstName: string;
  lastName: string;
  birthdate: string;
  fields: Record<string, string>;
}

const emptyChild = (): ChildForm => ({
  firstName: "",
  lastName: "",
  birthdate: "",
  fields: {},
});

interface SecondaryMemberForm {
  firstName: string;
  lastName: string;
  email: string;
}

const emptySecondaryMember = (): SecondaryMemberForm => ({
  firstName: "",
  lastName: "",
  email: "",
});

export type { CheckoutUpgrade } from "./helpers";

interface Props {
  product: ShopProductV3;
  live: LiveVariant;
  // Copies of the plan being purchased (multi-buy products); always 1 for
  // Kids Club, where the count comes from the children instead.
  quantity: number;
  initialEmail: string;
  primaryColor: string;
  onBack: () => void;
  onComplete: (summary: PurchaseSummary) => void;
  // Opens the screen-level sign-in dialog (the session bar owns it).
  onRequestLogin: () => void;
  // Present when this plan has an eligible, undismissed upgrade offer.
  upgrade?: CheckoutUpgrade;
}

// In-page checkout. Kids Club products (kind === "program") register the
// GUARDIAN as the account's primary member HOLDING one Kids Club plan
// instance per child, while each CHILD joins as a secondary minor member
// with no plan of their own. Other products subscribe the purchaser
// directly.
const CheckoutV3 = ({
  product,
  live,
  quantity,
  initialEmail,
  primaryColor,
  onBack,
  onComplete,
  onRequestLogin,
  upgrade,
}: Props) => {
  const kidsClub = product.kind === "program";
  const { planPackage, variant } = live;

  // RESERVATION plans are one-per-person: buying N collects N people (the
  // purchaser plus N−1 secondary members, one plan each). VOUCHER/BANK
  // plans stack all N copies on the purchaser.
  const planType = getPlanType(planPackage);
  const perMemberQuantity =
    !kidsClub &&
    quantity > 1 &&
    (planType === "RESERVATION" || planType === "GUEST_RESERVATION");

  const auth = useAuth();
  const { account, updateFetchAccountBffCache } = useAccount();
  const dispatch = useAppDispatch();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [children, setChildren] = useState<ChildForm[]>([emptyChild()]);
  const [secondaryMembers, setSecondaryMembers] = useState<
    SecondaryMemberForm[]
  >(() =>
    Array.from({ length: perMemberQuantity ? quantity - 1 : 0 }, () =>
      emptySecondaryMember(),
    ),
  );
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(
    planPackage.billingIntervals.includes("MONTHLY")
      ? "MONTHLY"
      : "FULL_SEASON",
  );
  const [autoRenewalConsentGiven, setAutoRenewalConsentGiven] = useState(false);
  const [purchaserFields, setPurchaserFields] = useState<
    Record<string, string>
  >({});
  const [submitting, setSubmitting] = useState(false);

  // Retry idempotency: a purchaser registered (and logged in) on a failed
  // attempt is reused instead of re-registered, and a subscription already
  // added on a logged-in attempt is never added (and charged) twice.
  const registeredPurchaserRef = useRef<{
    email: string;
    userId: number;
    loginResponse: JwtLoginSuccessResponse;
  } | null>(null);
  const subscriptionAddedRef = useRef<AccountDetails | null>(null);

  const { mutateAsync: addSubscription } = useMutation(addSubscriptionFetcher);
  const { mutateAsync: addMembers } = useMutation(addMembersFetcher);
  const { mutateAsync: updateBilling } = useMutation(updateBillingFetcher);

  const requiresRenewalConsent = planPackage.requireAutoRenewalConsent;
  const { data: autoRenewalAgreements } = useFetchAgreementsQuery(
    "AUTO_RENEWAL",
    requiresRenewalConsent ? planPackage.id : undefined,
    billingInterval,
  );

  // Upgrade contract: a plan change (offer applied or undone) voids any
  // prior auto-renewal acceptance, falls back to a billing interval the
  // new plan actually offers, and restarts the subscription step.
  useEffect(() => {
    setAutoRenewalConsentGiven(false);
    setBillingInterval((current) =>
      planPackage.billingIntervals.includes(current)
        ? current
        : planPackage.billingIntervals.includes("MONTHLY")
          ? "MONTHLY"
          : "FULL_SEASON",
    );
    subscriptionAddedRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPackage.id]);

  // Keep the per-pass member forms sized to the active plan: an upgrade
  // can flip a voucher-style plan to a reservation-style one (or back).
  useEffect(() => {
    const needed = perMemberQuantity ? quantity - 1 : 0;
    setSecondaryMembers((prev) =>
      prev.length === needed
        ? prev
        : needed < prev.length
          ? prev.slice(0, needed)
          : [
              ...prev,
              ...Array.from(
                { length: needed - prev.length },
                emptySecondaryMember,
              ),
            ],
    );
  }, [perMemberQuantity, quantity]);

  const memberCount = kidsClub ? children.length : quantity;
  const unitPrice =
    billingInterval === "MONTHLY"
      ? monthlyPriceOf(planPackage)
      : fullPriceOf(planPackage);
  const totalPrice = unitPrice * memberCount;
  const shouldBill = fullPriceOf(planPackage) * memberCount > 0;

  // Backend-priced total: the same signup-charge engine the purchase runs
  // (fees, proration, rolling billing), quoted for the exact plan instances
  // this checkout creates.  The catalog-derived totalPrice above remains the
  // display fallback while the quote is in flight.
  const { data: signupChargeQuote } = useQuery(
    ["v3SignupCharge", planPackage.id, billingInterval, memberCount],
    () =>
      fetchSignupChargeRequest({
        billingInterval,
        planPackageIds: new Array(memberCount).fill(planPackage.id),
      }),
    { enabled: shouldBill && memberCount > 0 },
  );
  const { totalCharge, totalRecurringCharge } =
    useSignupCharge(signupChargeQuote);
  // null = quote not loaded yet; the wallet button stays blocked until then.
  const quotedTotal = signupChargeQuote ? Number(totalCharge) : null;

  const amountLabel = useMemo(() => {
    if (!shouldBill) return "Free";
    if (quotedTotal === null) {
      const formatted = `${formatCurrency(totalPrice)}`;
      return billingInterval === "MONTHLY" ? `${formatted}/mo` : formatted;
    }
    if (billingInterval === "MONTHLY") {
      const recurring = Number(totalRecurringCharge);
      // A prorated first month differs from the recurring amount; say so
      // instead of labeling the prorated charge as the monthly price.
      return quotedTotal === recurring
        ? `${formatCurrency(quotedTotal)}/mo`
        : `${formatCurrency(quotedTotal)} today, then ${formatCurrency(
            recurring,
          )}/mo`;
    }
    return `${formatCurrency(quotedTotal)}`;
  }, [
    shouldBill,
    quotedTotal,
    totalRecurringCharge,
    totalPrice,
    billingInterval,
  ]);

  // Custom fields come from the backend user-fields feature (admin-
  // configured per tenant and per plan package), not the CMS.
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(planPackage.id);
  // Children get the plan's fields (e.g. jersey size); birthdate has a
  // dedicated input, so it's excluded here.
  const childUserFields = (planPackageUserFields ?? []).filter(
    (userField) => userField.field.name !== "birthdate",
  );
  // The purchaser gets the tenant's signup fields, plus the plan's fields
  // when they hold the plan for themselves (non-program products).
  const purchaserUserFields: AnyUserFieldDetails[] = [
    ...(tenantUserFields ?? []),
    ...(kidsClub ? [] : planPackageUserFields ?? []),
  ];

  const validate = (): string | null => {
    if (!auth.isLoggedIn) {
      if (!firstName.trim() || !lastName.trim())
        return kidsClub
          ? "Enter the guardian's first and last name."
          : "Enter your first and last name.";
      if (!isValidEmail(email)) return "Enter a valid email address.";
      if (password.length < 6)
        return "Password must be at least 6 characters.";
      if (password !== confirmPassword) return "Passwords do not match.";
      for (const userField of purchaserUserFields) {
        const value = purchaserFields[userField.field.name]?.trim();
        if (userField.required && !value)
          return `Enter ${userField.field.displayName.toLowerCase()}.`;
        if (value && userField.field.type === "DATE" && !isValidMaskedDate(value))
          return `Enter ${userField.field.displayName.toLowerCase()} as MM/DD/YYYY.`;
      }
    }
    if (kidsClub) {
      for (const [index, child] of children.entries()) {
        if (!child.firstName.trim() || !child.lastName.trim())
          return `Enter a first and last name for child ${index + 1}.`;
        if (!isValidMaskedDate(child.birthdate))
          return `Enter child ${index + 1}'s birthdate as MM/DD/YYYY.`;
        for (const userField of childUserFields) {
          const value = child.fields[userField.field.name]?.trim();
          if (userField.required && !value)
            return `Enter ${userField.field.displayName.toLowerCase()} for child ${index + 1}.`;
          if (value && userField.field.type === "DATE" && !isValidMaskedDate(value))
            return `Enter child ${index + 1}'s ${userField.field.displayName.toLowerCase()} as MM/DD/YYYY.`;
        }
      }
    }
    if (perMemberQuantity) {
      const purchaserEmail = (
        auth.isLoggedIn ? auth.username ?? "" : email
      )
        .trim()
        .toLowerCase();
      const seenEmails = new Set<string>([purchaserEmail]);
      for (const [index, member] of secondaryMembers.entries()) {
        if (!member.firstName.trim() || !member.lastName.trim())
          return `Enter a first and last name for pass ${index + 2}.`;
        if (!isValidEmail(member.email))
          return `Enter a valid email for pass ${index + 2}.`;
        const key = member.email.trim().toLowerCase();
        if (seenEmails.has(key))
          return `Each pass must belong to a different person — pass ${index + 2} repeats an email.`;
        seenEmails.add(key);
      }
    }
    if (
      requiresRenewalConsent &&
      (autoRenewalAgreements?.length ?? 0) > 0 &&
      !autoRenewalConsentGiven
    )
      return "Please accept the auto-renewal terms to continue.";
    return null;
  };

  const memberInput = (
    input: Pick<
      AddAccountMemberInput,
      "email" | "firstName" | "lastName" | "minor" | "userFields" | "planPackageIds"
    >,
  ): AddAccountMemberInput => ({
    phone: "",
    imageIdentifier: "",
    type: "SECONDARY",
    ...input,
  });

  // Adult secondary members for reservation multi-buys: one plan each.
  const secondaryMemberPayloads = (): AddAccountMemberInput[] =>
    secondaryMembers.map((member) =>
      memberInput({
        email: member.email.trim(),
        firstName: member.firstName.trim(),
        lastName: member.lastName.trim(),
        minor: false,
        userFields: {},
        planPackageIds: [planPackage.id],
      }),
    );

  // Backend userFields payload: masked MM/DD/YYYY dates become yyyy-MM-dd.
  // Blank values are omitted (unlike utils/userUtils.convertUserToUserFields,
  // which would serialize a blank DATE as today's date).
  const serializeUserFields = (
    values: Record<string, string>,
    fieldDefinitions: AnyUserFieldDetails[],
  ) => {
    const out: Record<string, string> = {};
    for (const userField of fieldDefinitions) {
      const raw = values[userField.field.name]?.trim();
      if (!raw) continue;
      out[userField.field.name] =
        userField.field.type === "DATE" ? toIsoDate(raw) : raw;
    }
    return out;
  };

  // Children are roster members only — the guardian (primary) holds the
  // plan instances, so each child carries no planPackageIds.
  const childMembers = (): AddAccountMemberInput[] =>
    children.map((child) =>
      memberInput({
        email: null,
        firstName: child.firstName.trim(),
        lastName: child.lastName.trim(),
        minor: true,
        userFields: {
          birthdate: toIsoDate(child.birthdate),
          ...serializeUserFields(child.fields, childUserFields),
        },
        planPackageIds: [],
      }),
    );

  // Plan instances held by the purchaser: one per child (Kids Club), one
  // for themselves on reservation multi-buys (secondaries hold their own),
  // or all N copies on voucher-style plans.
  const purchaserPlanIds = () =>
    new Array(
      kidsClub ? children.length : perMemberQuantity ? 1 : quantity,
    ).fill(planPackage.id) as number[];

  const completedSummary = (): PurchaseSummary => ({
    productName: product.name,
    variantLabel: `${variant.label}${quantity > 1 ? ` ×${quantity}` : ""}`,
    kidsClub,
    memberNames: kidsClub
      ? children.map((child) => `${child.firstName} ${child.lastName}`.trim())
      : perMemberQuantity
        ? secondaryMembers.map((member) =>
            `${member.firstName} ${member.lastName}`.trim(),
          )
        : [],
    amountLabel,
  });

  const purchaseAnonymously = async (token?: StripeTokenResponse) => {
    const trimmedEmail = email.trim();

    // Registration + login run once per email: a retry after a failed
    // account creation reuses the registered user instead of tripping the
    // exists check against the user we just created.
    let registered = registeredPurchaserRef.current;
    if (registered?.email !== trimmedEmail) {
      const exists = await userServices.userExists(trimmedEmail);
      if (exists) {
        toast.error(
          `An account with email ${trimmedEmail} already exists. Please log in to continue.`,
        );
        onRequestLogin();
        return false;
      }
      const purchaser: PrimaryUser = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: trimmedEmail,
        phone: phone.trim(),
        password,
        quantity: 1,
        imageIdentifier: "",
        minor: false,
        userFields: {},
      };
      const user = await userServices.register(
        purchaser,
        serializeUserFields(purchaserFields, purchaserUserFields),
      );
      const loginResponse = await setUserToken(purchaser);
      registered = {
        email: trimmedEmail,
        userId: user.id,
        loginResponse,
      };
      registeredPurchaserRef.current = registered;
    }

    // One request either way: the guardian/purchaser is the primary member
    // and holds every plan instance (one per child for Kids Club); children
    // ride along as plan-less secondary minor members.
    await accountServices.createAccount({
      type: "FULL",
      primaryUser: {
        userId: registered.userId,
        planPackageIds: purchaserPlanIds(),
      },
      members: kidsClub
        ? childMembers()
        : perMemberQuantity
          ? secondaryMemberPayloads()
          : [],
      token,
      billingInterval,
      source: "BUY_FLOW",
      emailMarketingConsent: false,
      smsMarketingConsent: false,
      autoRenewalAgreementId: autoRenewalConsentGiven
        ? autoRenewalAgreements?.[0]?.id
        : undefined,
    });
    dispatch(loginSucceed(registered.loginResponse));
    return true;
  };

  const purchaseLoggedIn = async (token?: StripeTokenResponse) => {
    if (!account || !auth.id) {
      toast.error("We could not load your account. Please try again.");
      return false;
    }
    // A signed-in shopper with no card on file enters one in the Stripe
    // form; save it to the account before the subscription charges it. The
    // cache update keeps the saved card rendered if a later step fails.
    if (token) {
      const billedAccount = await updateBilling({ accountId: account.id, token });
      updateFetchAccountBffCache(billedAccount);
    }
    // The guardian/purchaser holds the plan instances in both cases. The
    // subscription step is guarded against retries so a failure in the
    // add-members step below can never lead to a second charge.
    let updatedAccount = subscriptionAddedRef.current;
    if (!updatedAccount) {
      updatedAccount = await addSubscription({
        accountId: account.id,
        billingInterval,
        userInfo: [
          {
            userId: auth.id,
            planPackageIds: purchaserPlanIds(),
          },
        ],
        source: "BUY_FLOW",
        autoRenewalAgreementId: autoRenewalConsentGiven
          ? autoRenewalAgreements?.[0]?.id
          : undefined,
      });
      subscriptionAddedRef.current = updatedAccount;
    }
    if (kidsClub || perMemberQuantity) {
      updatedAccount = await addMembers({
        accountId: account.id,
        billingInterval,
        members: kidsClub ? childMembers() : secondaryMemberPayloads(),
        source: "BUY_FLOW",
      });
    }
    updateFetchAccountBffCache(updatedAccount);
    return true;
  };

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse,
  ): Promise<TokenReceivedOutcome> => {
    if (error) {
      toast.error(error.error.message);
      return "failed";
    }
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return "failed";
    }
    setSubmitting(true);
    try {
      const done = auth.isLoggedIn
        ? await purchaseLoggedIn(response)
        : await purchaseAnonymously(response);
      if (!done) return "failed";
      onComplete(completedSummary());
      return "completed";
    } catch (requestError: any) {
      // Backend failures are already toasted by the global response
      // interceptor with the ShopV3 messages from locale/en-US.ts (the
      // rejected value is the axios response). Only surface failures the
      // interceptor never saw, e.g. a network drop before a response.
      if (!requestError?.data?.code) {
        toast.error(
          "Something went wrong completing your purchase. Please try again.",
        );
      }
      return "failed";
    } finally {
      setSubmitting(false);
    }
  };

  const updateSecondaryMember = (
    index: number,
    patch: Partial<SecondaryMemberForm>,
  ) =>
    setSecondaryMembers((prev) =>
      prev.map((member, i) => (i === index ? { ...member, ...patch } : member)),
    );

  const updateChild = (index: number, patch: Partial<ChildForm>) =>
    setChildren((prev) =>
      prev.map((child, i) => (i === index ? { ...child, ...patch } : child)),
    );

  // One input per admin-configured user field, with the MM/DD/YYYY mask
  // for DATE fields; shared by the purchaser form and each child card.
  const renderUserFieldInput = (
    userField: AnyUserFieldDetails,
    values: Record<string, string>,
    onChange: (name: string, value: string) => void,
  ) => (
    <View key={userField.field.name}>
      <Spacer margin={4} />
      <Input
        label={`${userField.field.displayName}${
          userField.required ? "" : " (optional)"
        }`}
        placeholder={
          userField.field.type === "DATE"
            ? "MM/DD/YYYY"
            : userField.field.type === "DATETIME"
              ? "YYYY-MM-DD HH:mm:ss"
              : undefined
        }
        keyboardType={
          userField.field.type === "NUMBER" || userField.field.type === "DATE"
            ? "numeric"
            : "default"
        }
        maxLength={userField.field.type === "DATE" ? 10 : undefined}
        value={values[userField.field.name] ?? ""}
        onChangeText={(value: string) =>
          onChange(
            userField.field.name,
            userField.field.type === "DATE" ? formattedDate(value) : value,
          )
        }
      />
    </View>
  );

  const renderChildCard = (child: ChildForm, index: number) => (
    <View
      key={index}
      testID={`v3-child-${index}`}
      className="border rounded-2xl p-4 mb-3"
      style={{ borderColor: LINE, backgroundColor: PAPER }}
    >
      <View className="flex-row justify-between items-center">
        <Text category="s1">Child {index + 1}</Text>
        {children.length > 1 ? (
          <Pressable
            testID={`v3-remove-child-${index}`}
            onPress={() =>
              setChildren((prev) => prev.filter((_, i) => i !== index))
            }
          >
            <Text category="s2" status="danger">
              Remove
            </Text>
          </Pressable>
        ) : null}
      </View>
      <Spacer margin={4} />
      <Input
        label="First name"
        value={child.firstName}
        onChangeText={(value: string) =>
          updateChild(index, { firstName: value })
        }
      />
      <Spacer margin={4} />
      <Input
        label="Last name"
        value={child.lastName}
        onChangeText={(value: string) => updateChild(index, { lastName: value })}
      />
      <Spacer margin={4} />
      <Input
        label="Birthdate"
        placeholder="MM/DD/YYYY"
        keyboardType="numeric"
        maxLength={10}
        value={child.birthdate}
        onChangeText={(value: string) =>
          updateChild(index, { birthdate: formattedDate(value) })
        }
      />
      {childUserFields.map((userField) =>
        renderUserFieldInput(userField, child.fields, (name, value) =>
          updateChild(index, { fields: { ...child.fields, [name]: value } }),
        ),
      )}
    </View>
  );

  const consentSection = requiresRenewalConsent ? (
    <AutoRenewalConsentSection
      agreements={autoRenewalAgreements}
      consentGiven={autoRenewalConsentGiven}
      onChange={setAutoRenewalConsentGiven}
    />
  ) : null;

  // "+$4.75/mo per pass"-style difference between destination and source,
  // in the currently selected payment cadence.
  const upgradeDeltaLabel = (): string => {
    if (!upgrade) return "";
    const priceAt = (pp: PlanPackageDetails) =>
      billingInterval === "MONTHLY" ? monthlyPriceOf(pp) : fullPriceOf(pp);
    const delta =
      priceAt(upgrade.destinationLive.planPackage) -
      priceAt(upgrade.sourcePlanPackage);
    const suffix = billingInterval === "MONTHLY" ? "/mo" : "";
    const perPass = memberCount > 1 ? " per pass" : "";
    const sign = delta < 0 ? "−" : "+";
    return `${sign}${formatCurrency(Math.abs(delta))}${suffix}${perPass}`;
  };

  // Purchase stays disabled until the required auto-renewal terms are
  // accepted (mirrors phase two); validate() remains the backstop.
  const consentMissing =
    requiresRenewalConsent &&
    (autoRenewalAgreements?.length ?? 0) > 0 &&
    !autoRenewalConsentGiven;

  const completeLabel = shouldBill ? "Confirm Purchase" : "Complete Registration";

  return (
    <Container>
      <Pressable testID="v3-checkout-back" onPress={onBack} className="py-4">
        <Text category="s2" style={{ color: primaryColor }}>
          ← Back to {product.name}
        </Text>
      </Pressable>

      <View
        className="border rounded-2xl p-4 mb-4 flex-row justify-between items-center"
        style={{ borderColor: LINE, backgroundColor: PAPER }}
      >
        <View className="flex-1 pr-2">
          <Text category="s1">{product.name}</Text>
          <Text category="c1" appearance="hint">
            {variant.label}
            {kidsClub
              ? ` · ${memberCount} child${memberCount > 1 ? "ren" : ""}`
              : quantity > 1
                ? ` ×${quantity}`
                : ""}
          </Text>
        </View>
        <Text category="h6" style={{ color: primaryColor }}>
          {amountLabel}
        </Text>
      </View>

      {signupChargeQuote ? (
        <PaymentBreakdownV3
          quote={signupChargeQuote}
          totalCharge={Number(totalCharge)}
          totalRecurringCharge={Number(totalRecurringCharge)}
          monthly={billingInterval === "MONTHLY"}
          allInclusivePrice={planPackage.allInclusivePrice}
        />
      ) : null}

      {kidsClub ? (
        <View>
          <Text category="h6">Who is joining?</Text>
          <Text category="p2" appearance="hint" style={{ marginBottom: 10 }}>
            Every child added here receives {variant.label}. You manage all of
            them from one guardian account.
          </Text>
          {children.map(renderChildCard)}
          <Pressable
            testID="v3-add-child"
            onPress={() => setChildren((prev) => [...prev, emptyChild()])}
            className="mb-4"
          >
            <Text category="s2" style={{ color: primaryColor }}>
              + Add another child
            </Text>
          </Pressable>
        </View>
      ) : null}

      {perMemberQuantity ? (
        /* Reservation plans are one-per-person: collect who holds each of
           the additional passes. */
        <View>
          <Text category="h6">Who gets each pass?</Text>
          <Text category="p2" appearance="hint" style={{ marginBottom: 10 }}>
            Each pass belongs to one person. Pass 1 is yours; the people below
            join your account as members with their own pass.
          </Text>
          {secondaryMembers.map((member, index) => (
            <View
              key={index}
              testID={`v3-secondary-${index}`}
              className="border rounded-2xl p-4 mb-3"
              style={{ borderColor: LINE, backgroundColor: PAPER }}
            >
              <Text category="s1">Pass {index + 2}</Text>
              <Spacer margin={4} />
              <Input
                label="First name"
                value={member.firstName}
                onChangeText={(value: string) =>
                  updateSecondaryMember(index, { firstName: value })
                }
              />
              <Spacer margin={4} />
              <Input
                label="Last name"
                value={member.lastName}
                onChangeText={(value: string) =>
                  updateSecondaryMember(index, { lastName: value })
                }
              />
              <Spacer margin={4} />
              <Input
                label="Email"
                autoCapitalize="none"
                keyboardType="email-address"
                value={member.email}
                onChangeText={(value: string) =>
                  updateSecondaryMember(index, { email: value })
                }
              />
            </View>
          ))}
        </View>
      ) : null}

      {!auth.isLoggedIn ? (
        <View>
          <Text category="h6">
            {kidsClub ? "Guardian account" : "Your account"}
          </Text>
          <Text category="p2" appearance="hint" style={{ marginBottom: 10 }}>
            {kidsClub
              ? "The guardian holds the membership plan and manages it for every child on the account."
              : "This account manages your membership."}
          </Text>
          <Input
            label="First name"
            value={firstName}
            onChangeText={(value: string) => setFirstName(value)}
          />
          <Spacer margin={4} />
          <Input
            label="Last name"
            value={lastName}
            onChangeText={(value: string) => setLastName(value)}
          />
          <Spacer margin={4} />
          <Input
            label="Email"
            autoCapitalize="none"
            keyboardType="email-address"
            value={email}
            onChangeText={(value: string) => setEmail(value)}
          />
          <Spacer margin={4} />
          <Input
            label="Phone (optional)"
            keyboardType="phone-pad"
            value={phone}
            onChangeText={(value: string) => setPhone(value)}
          />
          {purchaserUserFields.map((userField) =>
            renderUserFieldInput(userField, purchaserFields, (name, value) =>
              setPurchaserFields((prev) => ({ ...prev, [name]: value })),
            ),
          )}
          <Spacer margin={4} />
          <Input
            label="Password"
            secureTextEntry
            value={password}
            onChangeText={(value: string) => setPassword(value)}
          />
          <Spacer margin={4} />
          <Input
            label="Confirm password"
            secureTextEntry
            value={confirmPassword}
            onChangeText={(value: string) => setConfirmPassword(value)}
          />
          <Spacer margin={4} />
        </View>
      ) : kidsClub ? (
        <Text category="p2" appearance="hint" style={{ marginBottom: 8 }}>
          You are registering as the guardian on your account.
        </Text>
      ) : null}

      {planPackage.billingIntervals.length > 1 && shouldBill ? (
        <View className="my-3">
          <Text category="h6" style={{ marginBottom: 6 }}>
            Payment schedule
          </Text>
          <View className="flex-row gap-6">
            <Radio
              testID="v3-interval-monthly"
              checked={billingInterval === "MONTHLY"}
              onChange={() => setBillingInterval("MONTHLY")}
            >
              Monthly
            </Radio>
            <Radio
              testID="v3-interval-full"
              checked={billingInterval === "FULL_SEASON"}
              onChange={() => setBillingInterval("FULL_SEASON")}
            >
              Pay in full
            </Radio>
          </View>
        </View>
      ) : null}

      {upgrade ? (
        <UpgradeTileV3
          upgrade={upgrade}
          deltaLabel={upgradeDeltaLabel()}
          primaryColor={primaryColor}
        />
      ) : null}

      <Spacer margin={6} />

      {shouldBill && (!auth.isLoggedIn || (account && !account.billingLast4)) ? (
        // Anonymous shoppers, and signed-in shoppers whose loaded account
        // has no card on file yet, enter a card; the logged-in path saves
        // it to the account before purchasing. While a signed-in account
        // is still loading, the confirm button below waits instead, so the
        // card form never flashes in front of a shopper who has a card.
        <StripePaymentForm
          onTokenReceived={handleTokenReceived}
          loading={submitting}
          disabled={consentMissing}
          beforeButton={consentSection}
          walletAmount={quotedTotal}
        />
      ) : (
        <View>
          {consentSection}
          {shouldBill && account?.billingLast4 ? (
            <>
              <CardOnFileV3 />
              <Text category="c1" appearance="hint" style={{ marginBottom: 8 }}>
                {i18n.t("components.CardOnFileV3.chargeNotice", {
                  amount: amountLabel,
                })}
              </Text>
            </>
          ) : null}
          <Button
            testID="v3-complete-purchase"
            loading={submitting || (shouldBill && !account)}
            disabled={consentMissing}
            onPress={() => handleTokenReceived(undefined, undefined)}
          >
            {completeLabel}
          </Button>
        </View>
      )}
      <Spacer margin={12} />
    </Container>
  );
};

export default CheckoutV3;
